import React from "react";
import { FiFileText } from "react-icons/fi";

import PageHero from "../../components/common/PageHero";
import PageSectionHeader from "../../components/common/PageSectionHeader";
import ScrollIndicator from "../Home/ScrollIndicator";
import useScrollToTop from "../../hooks/useScollToTop";

import DocumentsFilters from "./components/DocumentsFilters";
import DocumentsList from "./components/DocumentsList";
import DocumentsPagination from "./components/DocumentsPagination";
import PdfViewerModal from "./components/PdfViewerModal";

import { DOCUMENTS_PAGE } from "./data/documentsUiData";
import { useDocumentsPage } from "./hooks/useDocumentsPage";

export default function DocumentsPage() {
  useScrollToTop();

  const {
    loading,
    error,
    paginatedItems,
    total,
    allTotal,
    totalPages,
    years,
    categories,
    filters,
    page,
    selectedDocument,
    updateFilter,
    resetFilters,
    setLimit,
    setSort,
    setDir,
    goToPrevPage,
    goToNextPage,
    openDocument,
    closeDocument,
    refresh,
  } = useDocumentsPage();

  return (
    <>
      <PageHero
        title={DOCUMENTS_PAGE.title}
        imageUrl={DOCUMENTS_PAGE.heroImageUrl}
        crumbs={DOCUMENTS_PAGE.crumbs}
      />

      <ScrollIndicator />

      <main className="mx-auto max-w-[1240px] px-4 pb-16 pt-6 sm:px-6 md:pt-8">
        <PageSectionHeader
          eyebrow={DOCUMENTS_PAGE.eyebrow}
          title={DOCUMENTS_PAGE.title}
          description={DOCUMENTS_PAGE.description}
        >
          <div className="rounded-3xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              PDF publics
            </p>
            <p className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
              {loading ? "…" : allTotal}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              document{allTotal > 1 ? "s" : ""} disponible
              {allTotal > 1 ? "s" : ""}
            </p>
          </div>
        </PageSectionHeader>

        <div className="mt-5 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
              <FiFileText className="h-5 w-5" />
            </span>

            <div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-white">
                Tous les PDF publics de la commune
              </h2>
              <p className="mt-1 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
                Les documents peuvent être consultés directement depuis cette
                page, ouverts dans un nouvel onglet ou téléchargés.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <DocumentsFilters
            loading={loading}
            filters={filters}
            years={years}
            categories={categories}
            total={total}
            allTotal={allTotal}
            onChange={updateFilter}
            onReset={resetFilters}
            onRefresh={refresh}
            onLimit={setLimit}
            onSort={setSort}
            onDir={setDir}
          />
        </div>

        {error && (
          <div className="mt-5 rounded-3xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            Une erreur est survenue pendant le chargement des documents publics.
          </div>
        )}

        <section className="mt-6">
          <DocumentsList
            items={paginatedItems}
            loading={loading}
            onOpen={openDocument}
          />

          {!loading && total > 0 && (
            <DocumentsPagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={filters.limit}
              onPrev={goToPrevPage}
              onNext={goToNextPage}
              disabled={loading}
            />
          )}
        </section>
      </main>

      {selectedDocument && (
        <PdfViewerModal item={selectedDocument} onClose={closeDocument} />
      )}
    </>
  );
}